import { useEffect, useRef, useState } from "react";
import { inIframe, postToParent } from "../lib/embed";
import type { SessionMessage } from "../types";

const SOUND_URL = "/sounds/notification.wav";

/**
 * Does this browser have a meaningful notion of "visible but not focused"?
 *
 * On a desktop window it does, and it is the interesting case: a chat sitting behind
 * the mail client should still ring. A phone has no such state — whatever is on screen
 * IS what the user is looking at — and mobile browsers answer `document.hasFocus()`
 * badly: iOS Safari reports no focus whenever no element in the page holds it, which is
 * most of the time while someone reads a conversation. Asking there rang the bell on
 * every single agent message, mid-conversation included. So the question is only put to
 * the pointer types that can actually answer it; everywhere else `document.hidden` is
 * the whole truth.
 */
function focusIsMeaningful(): boolean {
  try {
    return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  } catch {
    return false;
  }
}

/**
 * New-message notifications for the public chat:
 *  - plays a sound when a new agent message arrives while the chat isn't visible,
 *  - shows an unread count in the tab title, e.g. "(1) …" (standalone page),
 *  - relays session id / unread count / "notify" to the embed parent so embed.js
 *    can badge + auto-reopen the launcher.
 *
 * Visibility is the document focus on the standalone page, or the embed panel
 * open/closed state (relayed by embed.js) inside the iframe.
 */
export function useMessageNotifications(
  publicId: string | undefined,
  messages: SessionMessage[],
) {
  const [unread, setUnread] = useState(0);
  const lastAgentTsRef = useRef<number | null>(null);
  const parentOpenRef = useRef(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const baseTitleRef = useRef<string>("");
  const sessionSentRef = useRef<string | null>(null);

  const framed = inIframe();
  // Sound can be disabled per-embed via ?sound=0.
  const soundEnabled =
    new URLSearchParams(window.location.search).get("sound") !== "0";

  const isVisible = () => {
    if (framed) return parentOpenRef.current;
    if (typeof document === "undefined" || document.hidden) return false;
    return focusIsMeaningful() ? document.hasFocus() : true;
  };

  // Lazily build the audio element.
  const getAudio = () => {
    if (!audioRef.current) {
      const a = new Audio(SOUND_URL);
      a.preload = "auto";
      a.volume = 0.5;
      audioRef.current = a;
    }
    return audioRef.current;
  };

  // Relay the session id to the parent once so embed.js can resume it later.
  useEffect(() => {
    if (publicId && sessionSentRef.current !== publicId) {
      sessionSentRef.current = publicId;
      postToParent({ type: "session", publicId });
    }
  }, [publicId]);

  // Track the embed panel open/closed state relayed by embed.js.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const data = e.data;
      if (!data || data.source !== "heyup-parent") return;
      if (data.type === "visibility") {
        parentOpenRef.current = !!data.open;
        if (data.open) setUnread(0);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Reset unread when the standalone tab regains focus / becomes visible.
  useEffect(() => {
    if (framed) return;
    const clear = () => {
      if (isVisible()) setUnread(0);
    };
    window.addEventListener("focus", clear);
    document.addEventListener("visibilitychange", clear);
    return () => {
      window.removeEventListener("focus", clear);
      document.removeEventListener("visibilitychange", clear);
    };
  }, [framed]);

  // Detect a newly-arrived agent message and notify.
  useEffect(() => {
    const agentTimestamps = messages
      .filter((m) => m.type === "agent" && m.value?.trim())
      .map((m) => m.timestamp);
    const maxTs = agentTimestamps.length ? Math.max(...agentTimestamps) : null;

    // First pass: set the baseline without notifying about existing history.
    if (lastAgentTsRef.current === null) {
      lastAgentTsRef.current = maxTs ?? 0;
      return;
    }

    if (maxTs !== null && maxTs > lastAgentTsRef.current) {
      lastAgentTsRef.current = maxTs;
      if (!isVisible()) {
        setUnread((n) => n + 1);
        if (soundEnabled) {
          getAudio()
            .play()
            .catch(() => {
              /* autoplay may be blocked before any gesture */
            });
        }
        postToParent({ type: "notify" });
      }
    }
  }, [messages, framed, soundEnabled]);

  // Reflect unread in the tab title (standalone) and to the parent (embed).
  useEffect(() => {
    postToParent({ type: "unread", count: unread });

    if (framed) return;
    if (!baseTitleRef.current) {
      baseTitleRef.current = document.title.replace(/^\(\d+\)\s*/, "");
    }
    document.title =
      unread > 0 ? `(${unread}) ${baseTitleRef.current}` : baseTitleRef.current;
  }, [unread, framed]);
}
